(() => {
  const STORAGE_KEY = 'ironside_recently_viewed_products_v1';
  const MAX_STORED_PRODUCTS = 20;
  const productCache = new Map();

  class IronsideRecentlyViewedProducts extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ironsideRecentlyViewedReady === 'true') return;

      this.dataset.ironsideRecentlyViewedReady = 'true';
      this.currentHandle = this.normalizeHandle(this.dataset.currentHandle || '');
      this.currentProductId = this.toPositiveNumber(this.dataset.currentProductId, 0);
      this.currentLooxRating = this.toRating(this.dataset.currentLooxRating || '');
      this.currentLooxRaters = this.toPositiveNumber(this.dataset.currentLooxRaters, 0);
      this.maxProducts = this.toPositiveNumber(this.dataset.maxProducts, 4);
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.currency = String(this.dataset.currency || 'CLP').toUpperCase();
      this.locale = this.dataset.locale || document.documentElement.lang || 'es-CL';
      this.trustBadgeUrl = this.dataset.trustBadgeUrl || '';
      this.showQuickAction = this.dataset.showQuickAction !== 'false';

      this.addEventListener('click', (event) => this.handleQuickActionClick(event));

      const run = () => this.init();

      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(run, { timeout: 1200 });
      } else {
        window.addEventListener('load', run, { once: true });
      }
    }

    async init() {
      const records = this.getStoredRecords()
        .filter((record) => record.handle && record.handle !== this.currentHandle)
        .slice(0, this.maxProducts);

      if (!records.length) {
        this.saveCurrentProduct();
        this.hideSection();
        return;
      }

      const products = (await Promise.all(records.map((record) => this.fetchProduct(record.handle, record))))
        .filter(Boolean)
        .slice(0, this.maxProducts);

      if (!products.length) {
        this.saveCurrentProduct();
        this.hideSection();
        return;
      }

      const itemsHtml = products
        .map((product) => `<div class="resource-list__item">${this.buildProductCard(product)}</div>`)
        .join('');

      this.querySelectorAll('[data-recently-viewed-list]').forEach((list) => {
        list.innerHTML = itemsHtml;
        list.setAttribute('data-has-recommendations', 'true');
      });

      this.removeAttribute('hidden');
      this.refreshLooxWidgets();
      this.saveCurrentProduct();
    }

    getStoredRecords() {
      try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : [];

        if (!Array.isArray(parsed)) return [];

        const records = parsed
          .map((item) => {
            if (typeof item === 'string') {
              return { handle: this.normalizeHandle(item) };
            }

            if (!item || typeof item !== 'object') return { handle: '' };

            return {
              handle: this.normalizeHandle(item.handle || ''),
              id: this.toPositiveNumber(item.id || item.productId, 0),
              looxRating: this.toRating(item.looxRating || item.rating || ''),
              looxRaters: this.toPositiveNumber(item.looxRaters || item.raters, 0),
              timestamp: this.toPositiveNumber(item.timestamp, 0)
            };
          })
          .filter((record) => record.handle)
          .filter((record, index, array) => array.findIndex((item) => item.handle === record.handle) === index)
          .slice(0, MAX_STORED_PRODUCTS);

        return records;
      } catch (_error) {
        return [];
      }
    }

    saveCurrentProduct() {
      if (!this.currentHandle) return;

      try {
        const previousRecords = this.getStoredRecords().filter((record) => record.handle !== this.currentHandle);
        const timestamp = Date.now();
        const currentRecord = {
          handle: this.currentHandle,
          id: this.currentProductId || undefined,
          looxRating: this.currentLooxRating || undefined,
          looxRaters: this.currentLooxRaters || undefined,
          timestamp
        };

        const next = [currentRecord, ...previousRecords].slice(0, MAX_STORED_PRODUCTS);
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch (_error) {
        return;
      }
    }

    async fetchProduct(handle, record = {}) {
      const normalizedHandle = this.normalizeHandle(handle);
      if (!normalizedHandle) return null;
      if (productCache.has(normalizedHandle)) {
        const cachedProduct = await productCache.get(normalizedHandle);
        return cachedProduct ? this.attachRecord(cachedProduct, record) : null;
      }

      const request = fetch(`/products/${encodeURIComponent(normalizedHandle)}.js`, {
        credentials: 'same-origin'
      })
        .then((response) => {
          if (!response.ok) return null;
          return response.json();
        })
        .catch(() => null);

      productCache.set(normalizedHandle, request);

      const product = await request;
      return product ? this.attachRecord(product, record) : null;
    }

    attachRecord(product, record) {
      return Object.assign({}, product, { __ironsideRecentlyViewedRecord: record || {} });
    }

    buildProductCard(product) {
      const rawTitle = product.title || '';
      const title = this.escapeHtml(rawTitle);
      const handle = this.normalizeHandle(product.handle || '');
      const url = this.getProductUrl(product, handle);
      const media = this.getProductMedia(product);
      const variants = this.getVariants(product);
      const pricing = this.getPricing(product);
      const discount = this.getDiscountData(product, pricing.price, pricing.compareAtPrice);
      const available = this.isProductAvailable(product);
      const statusBadgeHtml = this.buildStatusBadgeHtml(product, available);
      const saleBadgeHtml = this.buildSaleBadgeHtml(discount);
      const imageHtml = this.buildImageHtml(media, title);
      const quickActionHtml = this.showQuickAction
        ? this.buildQuickActionHtml(product, pricing.variant, variants, url, available)
        : '';
      const trustBadgeHtml = this.buildTrustBadgeHtml();
      const reviewHtml = this.buildReviewsHtml(product, url);
      const pricePrefixHtml = pricing.showFromLabel
        ? '<span class="ironside-rv-card__price-prefix">Desde</span>'
        : '';

      return `
        <article class="ironside-rv-card${media.secondary ? ' ironside-rv-card--has-secondary' : ''}" data-product-handle="${this.escapeAttribute(handle)}">
          <a class="ironside-rv-card__media" href="${this.escapeAttribute(url)}" aria-label="${title}">
            ${trustBadgeHtml}
            ${statusBadgeHtml}
            ${imageHtml}
          </a>
          ${quickActionHtml}
          <div class="ironside-rv-card__info">
            <a class="ironside-rv-card__title" href="${this.escapeAttribute(url)}">${title}</a>
            ${saleBadgeHtml}
            <div class="ironside-rv-card__price price">
              <span class="ironside-rv-card__price-current${discount.percent > 0 ? ' ironside-rv-card__price-current--sale' : ''}">${pricePrefixHtml}${this.formatMoney(pricing.price)}</span>
              ${pricing.compareAtPrice > pricing.price ? `<span class="ironside-rv-card__compare">${this.formatMoney(pricing.compareAtPrice)}</span>` : ''}
            </div>
            ${reviewHtml}
          </div>
        </article>
      `;
    }

    buildTrustBadgeHtml() {
      if (!this.trustBadgeUrl) return '';

      return `
        <img
          class="ironside-rv-card__trust-badge"
          src="${this.escapeAttribute(this.trustBadgeUrl)}"
          alt=""
          loading="lazy"
          decoding="async"
          aria-hidden="true"
        >
      `;
    }

    buildStatusBadgeHtml(product, available) {
      if (!available) {
        return '<span class="ironside-rv-card__status-badge ironside-rv-card__status-badge--sold-out">Agotado</span>';
      }

      if (this.isPreorderProduct(product)) {
        return '<span class="ironside-rv-card__status-badge ironside-rv-card__status-badge--preorder">Preventa</span>';
      }

      return '';
    }

    buildSaleBadgeHtml(discount) {
      if (!discount.percent) return '';

      const label = discount.isVariable ? `Hasta ${discount.percent}% OFF` : `${discount.percent}% OFF`;
      return `<span class="ironside-rv-card__sale-badge">${label}</span>`;
    }

    buildQuickActionHtml(product, selectedVariant, variants, url, available) {
      if (!available) return '';

      const icon = `
        <svg class="ironside-rv-card__quick-icon" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
          <path d="M6.2 7.3V5.9a3.8 3.8 0 0 1 7.6 0v1.4h1.3c.7 0 1.3.5 1.4 1.2l.8 7.1A2.2 2.2 0 0 1 15.1 18H4.9a2.2 2.2 0 0 1-2.2-2.4l.8-7.1c.1-.7.7-1.2 1.4-1.2h1.3Zm1.4 0h4.8V5.9a2.4 2.4 0 0 0-4.8 0v1.4Zm2.4 2.1a.7.7 0 0 1 .7.7v1.5h1.5a.7.7 0 1 1 0 1.4h-1.5v1.5a.7.7 0 1 1-1.4 0V13H7.8a.7.7 0 1 1 0-1.4h1.5v-1.5a.7.7 0 0 1 .7-.7Z" fill="currentColor"/>
        </svg>
      `;

      const hasSingleVariant = variants.length === 1;
      const variantId = selectedVariant && selectedVariant.id;

      if (hasSingleVariant && variantId) {
        return `
          <button
            class="ironside-rv-card__quick-action"
            type="button"
            data-rv-add-to-cart
            data-variant-id="${this.escapeAttribute(variantId)}"
            data-product-url="${this.escapeAttribute(url)}"
            aria-label="Agregar al carrito ${this.escapeAttribute(product.title || '')}"
          >
            ${icon}
            <span class="ironside-rv-card__quick-label">Agregar al carrito</span>
          </button>
        `;
      }

      return `
        <a class="ironside-rv-card__quick-action" href="${this.escapeAttribute(url)}" aria-label="Elegir opciones ${this.escapeAttribute(product.title || '')}">
          ${icon}
          <span class="ironside-rv-card__quick-label">Elegir opciones</span>
        </a>
      `;
    }

    buildReviewsHtml(product, url) {
      const record = product.__ironsideRecentlyViewedRecord || {};
      const productId = this.toPositiveNumber(product.id || record.id, 0);
      const rating = this.toRating(record.looxRating || record.rating || '');
      const raters = this.toPositiveNumber(record.looxRaters || record.raters, 0);

      if (!productId || !rating || !raters) return '';

      const safeRating = Math.max(0, Math.min(5, rating));
      const filledStars = Math.max(0, Math.min(5, Math.round(safeRating)));
      const stars = Array.from({ length: 5 })
        .map((_, index) => `<span class="ironside-rv-card__review-star" aria-hidden="true">${index < filledStars ? '&#9733;' : '&#9734;'}</span>`)
        .join('');
      const label = `${safeRating.toFixed(1)} de 5 estrellas, ${raters} reseñas`;

      return `
        <a class="ironside-rv-card__reviews" href="${this.escapeAttribute(url)}#looxReviews" aria-label="${this.escapeAttribute(label)}">
          <div class="loox-rating ironside-rv-card__loox-rating" data-id="${this.escapeAttribute(productId)}" data-rating="${this.escapeAttribute(safeRating)}" data-raters="${this.escapeAttribute(raters)}"></div>
          <span class="ironside-rv-card__review-fallback" aria-hidden="true">
            <span class="ironside-rv-card__review-stars">${stars}</span>
            <span class="ironside-rv-card__review-count">(${this.formatInteger(raters)})</span>
          </span>
        </a>
      `;
    }

    async handleQuickActionClick(event) {
      const button = event.target.closest('[data-rv-add-to-cart]');
      if (!button || !this.contains(button)) return;

      event.preventDefault();
      event.stopPropagation();

      const variantId = button.dataset.variantId;
      const productUrl = button.dataset.productUrl || '#';
      const label = button.querySelector('.ironside-rv-card__quick-label');
      const originalText = label ? label.textContent : '';

      if (!variantId || button.disabled) return;

      button.disabled = true;
      button.classList.add('is-loading');
      if (label) label.textContent = 'Agregando';

      try {
        const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
          ? window.Shopify.routes.root
          : '/';
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({ id: Number(variantId), quantity: 1 })
        });

        if (!response.ok) throw new Error('Cart add failed');

        const item = await response.json();

        if (label) label.textContent = 'Agregado';
        button.classList.add('is-added');

        document.dispatchEvent(new CustomEvent('ironside:recently-viewed:add-to-cart', {
          bubbles: true,
          detail: { item }
        }));
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { item } }));
        window.dispatchEvent(new CustomEvent('cart:update', { detail: { item } }));

        window.setTimeout(() => {
          button.disabled = false;
          button.classList.remove('is-loading', 'is-added');
          if (label) label.textContent = originalText || 'Agregar al carrito';
        }, 1800);
      } catch (_error) {
        window.location.href = productUrl;
      }
    }

    buildImageHtml(media, title) {
      if (!media.primary) {
        return '<div class="ironside-rv-card__image ironside-rv-card__image--empty" aria-hidden="true"></div>';
      }

      const primary = `
        <img
          class="ironside-rv-card__image ironside-rv-card__image--primary"
          src="${this.escapeAttribute(media.primary)}"
          alt="${title}"
          loading="lazy"
          decoding="async"
          sizes="(min-width: 990px) 33vw, (min-width: 750px) 50vw, 50vw"
        >
      `;

      const secondary = media.secondary
        ? `
          <img
            class="ironside-rv-card__image ironside-rv-card__image--secondary"
            src="${this.escapeAttribute(media.secondary)}"
            alt=""
            loading="lazy"
            decoding="async"
            aria-hidden="true"
            sizes="(min-width: 990px) 33vw, (min-width: 750px) 50vw, 50vw"
          >
        `
        : '';

      return `${primary}${secondary}`;
    }

    getProductUrl(product, handle) {
      if (typeof product.url === 'string' && product.url) return product.url;
      return handle ? `/products/${handle}` : '#';
    }

    getVariants(product) {
      return Array.isArray(product.variants) ? product.variants : [];
    }

    getPricing(product) {
      const variants = this.getVariants(product).filter((variant) => Number(variant.price || 0) > 0);
      const availableVariants = variants.filter((variant) => variant.available !== false);
      const pool = availableVariants.length ? availableVariants : variants;

      if (!pool.length) {
        const fallbackPrice = Number(product.price_min || product.price || 0);
        const fallbackCompareAtPrice = Number(product.compare_at_price_min || product.compare_at_price || 0);
        return {
          price: fallbackPrice,
          compareAtPrice: fallbackCompareAtPrice,
          variant: null,
          showFromLabel: false
        };
      }

      const minPrice = Math.min(...pool.map((variant) => Number(variant.price || 0)));
      const lowestPriceVariants = pool.filter((variant) => Number(variant.price || 0) === minPrice);
      const selectedVariant = lowestPriceVariants[0] || pool[0];
      const compareAtPrice = Math.max(...lowestPriceVariants.map((variant) => Number(variant.compare_at_price || 0)));

      return {
        price: minPrice,
        compareAtPrice,
        variant: selectedVariant,
        showFromLabel: variants.length > 1
      };
    }

    isProductAvailable(product) {
      const variants = this.getVariants(product);

      if (!variants.length) {
        return product.available !== false;
      }

      return variants.some((variant) => variant.available);
    }

    isPreorderProduct(product) {
      const tags = this.getProductTags(product);
      return tags.some((tag) => {
        const normalized = tag.toLowerCase();
        return normalized.includes('preventa') || normalized.includes('pre-order') || normalized.includes('preorder');
      });
    }

    getProductTags(product) {
      if (Array.isArray(product.tags)) return product.tags.map((tag) => String(tag));
      if (typeof product.tags === 'string') return product.tags.split(',').map((tag) => tag.trim()).filter(Boolean);
      return [];
    }

    getProductMedia(product) {
      const images = Array.isArray(product.images) ? product.images : [];
      const primary = this.normalizeImageUrl(product.featured_image || images[0] || '');
      const secondary = images.length > 1 ? this.normalizeImageUrl(images[1]) : '';

      return {
        primary,
        secondary: secondary && secondary !== primary ? secondary : ''
      };
    }

    normalizeImageUrl(image) {
      let imageUrl = image;

      if (imageUrl && typeof imageUrl === 'object' && imageUrl.src) {
        imageUrl = imageUrl.src;
      }

      if (typeof imageUrl !== 'string' || !imageUrl) return '';

      if (imageUrl.startsWith('//')) {
        return `${window.location.protocol}${imageUrl}`;
      }

      return imageUrl;
    }

    getDiscountData(product, displayPrice, displayCompareAtPrice) {
      const variants = this.getVariants(product);
      const discounts = variants
        .map((variant) => {
          const price = Number(variant.price || 0);
          const compareAtPrice = Number(variant.compare_at_price || 0);
          return this.getDiscountPercent(price, compareAtPrice);
        })
        .filter((percent) => percent > 0);

      const fallbackPercent = this.getDiscountPercent(displayPrice, displayCompareAtPrice);
      const percent = discounts.length ? Math.max(...discounts) : fallbackPercent;

      if (!percent) {
        return { percent: 0, isVariable: false };
      }

      const uniqueDiscounts = [...new Set(discounts)];
      const hasVariablePrice = Number(product.price_min || 0) !== Number(product.price_max || product.price_min || 0);
      const isVariable = variants.length > 1 && (uniqueDiscounts.length > 1 || hasVariablePrice);

      return { percent, isVariable };
    }

    getDiscountPercent(price, compareAtPrice) {
      const numericPrice = Number(price || 0);
      const numericCompareAtPrice = Number(compareAtPrice || 0);

      if (!numericCompareAtPrice || numericCompareAtPrice <= numericPrice) return 0;

      const percentage = ((numericCompareAtPrice - numericPrice) / numericCompareAtPrice) * 100;
      return Math.max(1, Math.round(percentage));
    }

    formatMoney(cents) {
      const amount = Number(cents || 0) / 100;
      const decimals = this.currency === 'CLP' ? 0 : 2;
      const locale = this.currency === 'CLP' ? 'es-CL' : this.locale;
      const symbol = this.getCurrencySymbol();

      try {
        const formattedAmount = new Intl.NumberFormat(locale, {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals
        }).format(amount);

        return `${symbol}${formattedAmount}`.replace(/\s+/g, '');
      } catch (_error) {
        return `${symbol}${Math.round(amount).toLocaleString('es-CL')}`;
      }
    }

    getCurrencySymbol() {
      if (this.currency === 'CLP') return '$';

      const format = String(this.moneyFormat || '');
      const withoutAmount = format
        .replace(/\{\{\s*amount(?:_no_decimals|_with_comma_separator|_no_decimals_with_comma_separator)?\s*\}\}/gi, '')
        .replace(/\b(CLP|MXN|USD|ARS|COP|PEN|BRL)\b/gi, '')
        .trim();

      if (withoutAmount) return withoutAmount;
      if (this.currency === 'USD') return 'US$';
      return '$';
    }

    formatInteger(value) {
      try {
        return new Intl.NumberFormat(this.locale || 'es-CL', {
          maximumFractionDigits: 0
        }).format(Number(value || 0));
      } catch (_error) {
        return String(Math.round(Number(value || 0)));
      }
    }

    refreshLooxWidgets() {
      window.setTimeout(() => {
        try {
          const Loox = window.Loox || window.loox;

          if (Loox && typeof Loox.refresh === 'function') {
            Loox.refresh();
          }

          if (Loox && Loox.ratings && typeof Loox.ratings.refresh === 'function') {
            Loox.ratings.refresh();
          }

          document.dispatchEvent(new CustomEvent('loox:refresh', { bubbles: true }));
          window.dispatchEvent(new CustomEvent('loox:refresh'));
        } catch (_error) {
          return;
        }
      }, 0);
    }

    hideSection() {
      this.setAttribute('hidden', '');
    }

    toPositiveNumber(value, fallback) {
      const number = Number(value);
      return Number.isFinite(number) && number > 0 ? number : fallback;
    }

    toRating(value) {
      const number = Number(value);
      return Number.isFinite(number) && number > 0 ? Math.min(5, number) : 0;
    }

    normalizeHandle(value) {
      return String(value || '')
        .trim()
        .replace(/^\/products\//, '')
        .split('?')[0]
        .split('#')[0]
        .replace(/^\/+|\/+$/g, '');
    }

    escapeHtml(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    }

    escapeAttribute(value) {
      return this.escapeHtml(value);
    }
  }

  if (!customElements.get('ironside-recently-viewed-products')) {
    customElements.define('ironside-recently-viewed-products', IronsideRecentlyViewedProducts);
  }
})();